import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStoredAuth } from '../authStorage';
import { hasFrontendPermission } from '../permissions';
import { useAppState } from '../state/AppStateContext';
import { useToast } from '../components/ui/ToastProvider';
import { useViewportMode } from '../utils/useViewportMode';
import { MenuButton } from '../components/ui/MenuButton';

const PAGE_META = {
  '/dispatch':  { label: 'Dispatch', sub: 'Command Center' },
  '/dashboard': { label: 'Dashboard', sub: null },
  '/jobs':      { label: 'Jobs',     sub: null },
  '/schedule':  { label: 'Schedule', sub: null },
  '/customers': { label: 'Customers', sub: null },
  '/activity':  { label: 'Activity', sub: null },
  '/invoices':  { label: 'Invoices', sub: null },
  '/inventory': { label: 'Inventory', sub: null },
  '/projects':  { label: 'Projects', sub: null },
  '/project-planner': { label: 'Planner', sub: null },
  '/quotes':    { label: 'Quotes', sub: null },
  '/team':      { label: 'Team',     sub: null },
  '/users':     { label: 'Users',    sub: null },
  '/analytics': { label: 'Analytics', sub: null },
  '/settings':  { label: 'Settings', sub: null },
};

const SEARCH_ITEMS = [
  { to: '/dispatch', label: 'Dispatch', group: 'Primary', keywords: ['command center', 'queue', 'timeline'], permission: 'dispatch.manage' },
  { to: '/dashboard', label: 'Dashboard', group: 'Primary', keywords: ['overview', 'kpi', 'risk'] },
  { to: '/jobs', label: 'Jobs', group: 'Main', keywords: ['work orders', 'queue', 'field'], permission: 'jobs.manage' },
  { to: '/schedule', label: 'Schedule', group: 'Main', keywords: ['calendar', 'dispatch', 'agenda'], permission: 'dispatch.manage' },
  { to: '/customers', label: 'Customers', group: 'Operations', keywords: ['accounts', 'directory'], permission: 'customers.manage' },
  { to: '/activity', label: 'Activity', group: 'Operations', keywords: ['feed', 'log'], permission: 'customers.manage' },
  { to: '/invoices', label: 'Invoices', group: 'Operations', keywords: ['billing', 'payments'], permission: 'invoices.manage' },
  { to: '/projects', label: 'Projects', group: 'Operations', keywords: ['portfolio', 'delivery'], permission: 'projects.manage' },
  { to: '/project-planner', label: 'Planner', group: 'Operations', keywords: ['gantt', 'timeline', 'planning'], permission: 'projects.manage' },
  { to: '/team', label: 'Team', group: 'Operations', keywords: ['technicians', 'roster'], permission: 'technicians.manage' },
  { to: '/inventory', label: 'Inventory', group: 'Tools', keywords: ['stock', 'warehouse'], permission: 'inventory.manage' },
  { to: '/quotes', label: 'Quotes', group: 'Tools', keywords: ['estimates', 'proposals'], permission: 'quotes.manage' },
  { to: '/analytics', label: 'Analytics', group: 'Tools', keywords: ['reports', 'exports', 'metrics'], permission: 'exports.view' },
  { to: '/users', label: 'Users', group: 'Admin', keywords: ['accounts', 'permissions'], permission: 'accounts.manage' },
  { to: '/settings', label: 'Settings', group: 'Admin', keywords: ['configuration', 'rules'], permission: 'dispatch.manage' },
];

const MenuIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
    <path d="M1.5 3.5h12M1.5 7.5h12M1.5 11.5h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M6.5 1.5v10M1.5 6.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M9.5 9.5l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

export function AppTopBar() {
  const { setMobileNavOpen } = useAppState();
  const { pushToast } = useToast();
  const mode = useViewportMode();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = mode === 'mobile';
  const auth = loadStoredAuth();
  const user = auth?.user;
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);

  const meta = PAGE_META[location.pathname]
    ?? (location.pathname.startsWith('/project-planner/') ? PAGE_META['/project-planner'] : null)
    ?? { label: 'FieldOps', sub: null };
  const menuItems = [
    hasFrontendPermission(user, 'jobs.manage')
      ? { id: 'new-job', label: 'Create Job', onSelect: () => { navigate('/jobs?compose=1'); pushToast('Job composer opened', 'success'); } }
      : null,
    hasFrontendPermission(user, 'customers.manage')
      ? { id: 'new-customer', label: 'Add Customer', onSelect: () => { navigate('/customers?compose=1'); pushToast('Customer composer opened', 'success'); } }
      : null,
    hasFrontendPermission(user, 'quotes.manage')
      ? { id: 'new-quote', label: 'Create Quote', onSelect: () => { navigate('/quotes?compose=1'); pushToast('Quote composer opened', 'success'); } }
      : null,
    hasFrontendPermission(user, 'exports.view')
      ? { id: 'export', label: 'Open Analytics', onSelect: () => { navigate('/analytics'); pushToast('Analytics opened', 'info'); } }
      : null,
  ].filter(Boolean);
  const visibleSearchItems = useMemo(
    () => SEARCH_ITEMS.filter((item) => !item.permission || hasFrontendPermission(user, item.permission)),
    [user],
  );
  const filteredSearchItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return visibleSearchItems;
    return visibleSearchItems.filter((item) => (
      [item.label, item.group, ...(item.keywords || [])]
        .some((value) => String(value || '').toLowerCase().includes(term))
    ));
  }, [searchTerm, visibleSearchItems]);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const frame = window.requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [searchOpen]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const isShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k';
      if (isShortcut) {
        event.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const tag = String(event.target?.tagName || '').toLowerCase();
        const isTypingTarget = tag === 'input' || tag === 'textarea' || event.target?.isContentEditable;
        if (!isTypingTarget) {
          event.preventDefault();
          setSearchOpen(true);
        }
      }
      if (event.key === 'Escape') {
        setSearchOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const openSearch = () => setSearchOpen(true);
  const closeSearch = () => {
    setSearchOpen(false);
    setSearchTerm('');
  };
  const handleSearchSelect = (item) => {
    navigate(item.to);
    closeSearch();
  };

  return (
    <>
      <header className="app-topbar" role="banner">
        <div className="app-topbar-left">
          {isMobile && (
            <button
              type="button"
              className="ui-button ui-button--ghost touch-target"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation"
              style={{ padding: '0 8px' }}
            >
              <MenuIcon />
            </button>
          )}

          <nav className="app-topbar-breadcrumb" aria-label="Breadcrumb">
            <span className="app-topbar-breadcrumb-team">FieldOps</span>
            <span className="app-topbar-breadcrumb-sep" aria-hidden="true">/</span>
            <span className="app-topbar-breadcrumb-page">{meta.label}</span>
            {meta.sub && (
              <>
                <span className="app-topbar-breadcrumb-sep" aria-hidden="true">/</span>
                <span className="app-topbar-breadcrumb-page" style={{ color: 'var(--text-muted)' }}>
                  {meta.sub}
                </span>
              </>
            )}
          </nav>
        </div>

        <div className="app-topbar-actions">
          {user ? (
            <div className="app-topbar-user">
              <span className="app-topbar-user-name">{user.username}</span>
              <span className="app-topbar-user-role">{String(user.role || '').toUpperCase()}</span>
            </div>
          ) : null}
          <button
            type="button"
            className="ui-button ui-button--ghost app-search-button"
            aria-label="Search routes"
            style={{ padding: '0 9px' }}
            onClick={openSearch}
          >
            <SearchIcon />
            {!isMobile ? <span className="app-search-shortcut">Ctrl K</span> : null}
          </button>

          {menuItems.length ? (
            <MenuButton
              label={
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <PlusIcon />
                  <span>New</span>
                </span>
              }
              items={menuItems}
            />
          ) : null}
        </div>
      </header>

      {searchOpen ? (
        <div className="app-search-overlay" role="presentation" onClick={closeSearch}>
          <div
            className="app-search-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Search routes"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="app-search-head">
              <SearchIcon />
              <input
                ref={searchInputRef}
                className="app-search-input"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search pages, tools, and workspaces"
              />
              <button type="button" className="app-search-close" onClick={closeSearch}>
                Esc
              </button>
            </div>
            <div className="app-search-results">
              {filteredSearchItems.length ? (
                filteredSearchItems.map((item) => (
                  <button
                    key={item.to}
                    type="button"
                    className="app-search-result"
                    onClick={() => handleSearchSelect(item)}
                  >
                    <span className="app-search-result-main">{item.label}</span>
                    <span className="app-search-result-meta">{item.group}</span>
                  </button>
                ))
              ) : (
                <div className="app-search-empty">
                  No matching routes for "{searchTerm.trim()}".
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
